import { useState } from "react";
import { Bouton } from "@/composants/ui/bouton";
import { Erreur } from "@/composants/ui/etats";
import { messageDe } from "@/lib/chargement";
import { api, type Lot } from "@/lib/api";

/**
 * La pièce qui prouve la réalisation d'une vente sans pose (ADR-0033).
 *
 *     « Pour une vente sans pose, c'est donc le bon d'enlèvement signé, ou le
 *       BL signé dans le cas d'une livraison, qui matérialise la réalisation. »
 *
 * Avec pose, la preuve est le PV de réception initiale signé : elle viendra
 * avec l'écran de réception, au LOT 5.
 */
export function piecePourCloturer(lot: Pick<Lot, "mode_remise" | "avec_pose">) {
  if (lot.avec_pose) return null;
  return lot.mode_remise === "EMPORT"
    ? { categorie: "BON_ENLEVEMENT_SIGNE", libelle: "Bon d'enlèvement signé" }
    : { categorie: "BL_SIGNE", libelle: "Bon de livraison signé" };
}

/**
 * Déposer le document signé et clôturer le lot : un seul geste à l'écran.
 *
 * Deux appels, dans cet ordre. La clôture reste une décision humaine — ce
 * bouton en est une — et c'est la base qui vérifie la preuve
 * (`trg_lots_cloture`) : l'écran ne la redouble pas. Si la clôture est
 * refusée, le document reste déposé et le message du trigger dit pourquoi.
 */
export function CloturerSurPreuve({
  lot,
  onClos,
}: {
  lot: Lot;
  onClos: () => Promise<void>;
}) {
  const piece = piecePourCloturer(lot);
  const [fichier, setFichier] = useState<File | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  if (!piece) return null;
  const idChamp = `preuve-${lot.id}`;

  return (
    <form
      className="mt-4 flex flex-col gap-3 border-t border-filet pt-4"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        if (!fichier) return;
        setErreur(null);
        setEnvoi(true);
        try {
          await api.deposerDocument({ lot_id: lot.id }, piece.categorie, fichier);
          await api.corrigerLot(lot.id, { statut: "CLOTURE_OPERATIONNELLE" });
          await onClos();
        } catch (cause) {
          setErreur(messageDe(cause));
        } finally {
          setEnvoi(false);
        }
      }}
    >
      {erreur ? <Erreur message={erreur} /> : null}
      <div className="flex flex-col gap-1.5">
        <label htmlFor={idChamp} className="text-sm font-medium text-encre">
          {piece.libelle}
        </label>
        <input
          id={idChamp}
          type="file"
          required
          accept=".pdf,.jpg,.jpeg,.png,.heic,.webp"
          onChange={(evenement) => setFichier(evenement.target.files?.[0] ?? null)}
          className="min-w-0 text-sm text-encre file:mr-3 file:rounded-[6px] file:border file:border-filet file:bg-white file:px-3 file:py-2 file:text-sm file:text-encre"
        />
        <p className="text-xs text-encre-douce">
          Le document signé par le client prouve la réalisation : le déposer clôt ce suivi.
        </p>
      </div>
      <div>
        <Bouton type="submit" variante="principal" disabled={envoi || !fichier}>
          {envoi ? "Clôture en cours…" : "Déposer et clôturer"}
        </Bouton>
      </div>
    </form>
  );
}
